import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Check, ArrowRight, Mail } from 'lucide-react'
import SEO from '../components/seo/SEO'
import Button from '../components/ui/Button'
import { useLocalePath } from '../hooks/useLocalePath'
import styles from './Pricing.module.css'

const FEATURE_KEYS = [
  'generation',
  'baseline',
  'evaluation',
  'mrv',
  'support',
  'deliverables',
] as const

export default function Pricing() {
  const { t } = useTranslation('pricing')
  const { t: tSeo } = useTranslation('seo')
  const lp = useLocalePath()

  return (
    <div className={styles.page}>
      <SEO title={tSeo('pricing.title')} description={tSeo('pricing.description')} canonical="/pricing" />

      <section className={styles.hero}>
        <div className={styles.container}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="eyebrow">{t('hero.eyebrow')}</span>
            <h1>{t('hero.title')}</h1>
            <p className={styles.lead}>{t('hero.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <section className={styles.cardSection}>
        <div className={styles.container}>
          <motion.div
            className={styles.pricingCard}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className={styles.cardAccent} />
            <span className={styles.cardLabel}>{t('card.label')}</span>
            <div className={styles.priceRow}>
              <span className={styles.priceValue}>{t('card.price')}</span>
              <span className={styles.priceUnit}>{t('card.unit')}</span>
            </div>
            <p className={styles.cardDesc}>{t('card.description')}</p>

            <ul className={styles.features}>
              {FEATURE_KEYS.map(key => (
                <li key={key}>
                  <Check size={18} />
                  <span>{t(`features.${key}`)}</span>
                </li>
              ))}
            </ul>

            <Button variant="secondary" size="lg" href={lp('/contact')} className={styles.cardCta}>
              {t('cta')} <ArrowRight size={18} />
            </Button>

            <p className={styles.volumeNote}>{t('volumeNote')}</p>
          </motion.div>

          <motion.div
            className={styles.enterpriseBlock}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p>{t('enterpriseNote')}</p>
            <a href={lp('/contact')} className={styles.enterpriseLink}>
              <Mail size={16} /> {t('enterpriseLink')}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
